import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DestroyRef,
  ElementRef,
  NgZone,
  QueryList,
  TemplateRef,
  ViewEncapsulation,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DateTime } from 'luxon';
import { HumanDatePipe } from '../../../pipes/humandate.pipe';
import { TemplateForDirective } from './template-for.directive';
import { InputComponent } from '../../../forms/input/input.component';
import { SpinnerComponent } from '../../../spinner/spinner.component';
import { findScrollContainer, requestScrollPadding, sentinelPassedTop, stickyInset } from './sticky-observer';

export type ColumnType = 'text' | 'template' | 'datetime' | 'date' | 'number' | 'actions';

export interface IColumnDef {
  headerText: string;
  field?: string;
  /** How the cell renders; `template` uses the `appTemplateFor` template named after `field`. */
  columnType?: ColumnType | string;
  /** Cell and header alignment; numbers and actions default to `end`. */
  align?: 'start' | 'end';
  /** Fixed column width, e.g. '3.5rem' or '30%'. */
  width?: string;
  /** Hides a low-priority column below 640px (`sm`), below 1024px (`md`) or at 1440px and narrower (`lg`). */
  hideBelow?: 'sm' | 'md' | 'lg';
  /**
   * Marks a column the table may hide to fit its box. When the columns are
   * wider than the box, these hide lowest first, before the table scrolls
   * sideways; they come back when there is room again.
   */
  dropOrder?: number;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc';
  /** Direction of the first sort on this column; later clicks flip it. Defaults to `asc`. */
  firstSortDirection?: 'asc' | 'desc';
  /** The only direction this column sorts in; clicking it again changes nothing. */
  fixedSortDirection?: 'asc' | 'desc';
  editable?: boolean;
  /** @deprecated use `width` */
  widthZero?: boolean;
  /** @deprecated use `width` */
  truncate?: '200';
}

export interface ITableGroup {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  header: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: Record<string, any>[];
  expanded?: boolean;
}

export interface ISortOutput {
  field: string;
  direction: 'asc' | 'desc';
}

/** A row dropped before or after another row (see `rowReorderable`). */
export interface IRowReorderOutput {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  target: Record<string, any>;
  place: 'before' | 'after';
}

export interface IInlineEditOutput {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: Record<string, any>;
  field: string;
  newValue: string;
  originalValue: string;
}

type CellKind = 'template' | 'datetime' | 'date' | 'number' | 'text' | 'empty';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Row = Record<string, any>;

@Component({
  selector: 'app-content-table-cabinet',
  templateUrl: './content-table.component.html',
  styleUrl: './content-table.component.scss',
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [NgTemplateOutlet, HumanDatePipe, DatePipe, FormsModule, InputComponent, SpinnerComponent],
  host: {
    '[class.density-compact]': "density() === 'compact'",
    '[class.sticky-header]': 'stickyHeader()',
    '[class.pin-header]': 'pinHeader() && !stickyHeader()',
    '[class.scrolls-x]': 'scrollsX()',
    '[class.row-hover]': 'hover()',
    '[class.is-loading]': 'loading()',
  },
})
export class ContentTableCabinetComponent implements AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  columns = input<IColumnDef[]>([]);
  data = input<Row[]>([]);
  groups = input<ITableGroup[]>([]);
  groupable = input<boolean>(false);

  density = input<'comfortable' | 'compact'>('comfortable');
  /** Row property that identifies a row (for `selectedKey`). */
  rowKey = input<string>('id');
  /** Key of the row shown as selected, e.g. the item whose details are open. */
  selectedKey = input<string | number | null>(null);
  /** Controlled sort; when set it decides which header shows the sort arrow. */
  sortState = input<ISortOutput | null>(null);
  /** Makes the table its own scroller with the header row pinned. */
  stickyHeader = input(false);
  /**
   * Pins the header row to the top of the scroll area around the table (the
   * page, a dialog body) while the table runs past it. On by default.
   */
  pinHeader = input(true);
  /** Visually hidden table caption. */
  caption = input<string>('');
  /** Neutral row hover; off for read-only key/value tables. */
  hover = input(true);
  /** Dims the rows under a spinner while data reloads. */
  loading = input(false);
  /** Shown in a full-width row when there are no rows (empty, no results, error). */
  stateTemplate = input<TemplateRef<unknown> | null>(null);
  /** Result summary announced politely when it changes, e.g. "52 projects". */
  statusText = input<string>('');
  /** Announced while `loading` is on. */
  loadingText = input<string>('Loading');
  /** Placeholder rows shown while the first page loads. */
  skeletonRows = input<number>(5);
  /** Key of the row whose `appTemplateFor="rowDetail"` template shows in a full-width row below it. */
  expandedKey = input<string | number | null>(null);
  /**
   * Adds a leading checkbox column for choosing several rows: a box per row
   * (Shift+click checks a range) and a select-all box for the rows shown.
   * Separate from `selectedKey`, which marks the one row whose details are open.
   */
  selectable = input(false);
  /** Keys (by `rowKey`) of the checked rows. */
  selectedKeys = model<readonly (string | number)[]>([]);
  /** Row property that names a row in its checkbox label ("Select <name>"). */
  rowLabelKey = input<string>('name');
  /** Lets rows be dragged (not while a cell is being edited); `rowDragStart` fills the drag data. */
  rowDraggable = input(false);
  /**
   * Lets a dragged row be dropped before or after another row; a line marks
   * where it will go and `rowReorder` reports the drop. Needs `rowDraggable`.
   */
  rowReorderable = input(false);
  /** Whether the dragged row may be dropped next to `target`; any row when not set. */
  canReorder = input<((item: Row, target: Row) => boolean) | null>(null);

  /** The row being dragged for a reorder, and its element. */
  private reorderItem: Row | null = null;
  private reorderRow: HTMLElement | null = null;
  /** The row element showing the drop line, and on which edge. */
  private dropMark: { row: HTMLElement; place: 'before' | 'after' } | null = null;
  /** Key of the row that has just been moved; it shows a ring for a moment. */
  readonly movedKey = signal<unknown>(null);
  private movedTimer: ReturnType<typeof setTimeout> | null = null;

  /** Checked keys, for lookups. */
  readonly checkedKeys = computed(() => new Set<unknown>(this.selectedKeys()));
  /** How many of the rows shown are checked. */
  readonly pageSelection = computed<'none' | 'some' | 'all'>(() => {
    const keys = this.data().map((row) => row[this.rowKey()]);
    const checked = keys.filter((key) => this.checkedKeys().has(key)).length;
    if (checked === 0) return 'none';
    return checked === keys.length ? 'all' : 'some';
  });
  /** Columns including the checkbox column, for full-width rows. */
  readonly columnCount = computed(() => this.columns().length + (this.selectable() ? 1 : 0));

  /** The row last checked or unchecked by a click, where a Shift+click range starts. */
  private rangeAnchor: unknown = null;

  private readonly sortMessage = signal('');

  /** Text of the polite live region: loading, then the result summary and the last sort. */
  readonly liveMessage = computed(() => {
    if (this.loading()) return this.loadingText();
    return [this.statusText(), this.sortMessage()].filter(Boolean).join('. ');
  });

  /** The table is wider than its box and scrolls sideways (narrow screens). */
  readonly scrollsX = signal(false);

  /** Columns with a `dropOrder`, in the order they hide. */
  private readonly optionalColumns = computed(() =>
    this.columns()
      .filter((col) => col.dropOrder !== undefined)
      .sort((a, b) => (a.dropOrder ?? 0) - (b.dropOrder ?? 0)),
  );
  /** Optional columns hidden so the table fits its box. */
  private readonly droppedColumns = signal<readonly IColumnDef[]>([]);
  private readonly droppedSet = computed(() => new Set(this.droppedColumns()));
  // What the last fit was worked out for; a change starts it again with every column shown.
  private fitBoxWidth: number | null = null;
  private fitData: readonly unknown[] | null = null;

  /** Sideways-scrolling tables can't pin their own header to the page; a copy above them is pinned instead. */
  readonly showPinnedCopy = computed(() => this.scrollsX() && this.pinHeader() && !this.stickyHeader());

  private readonly scrollBox = viewChild<ElementRef<HTMLElement>>('scroll');
  private readonly tableBox = viewChild<ElementRef<HTMLTableElement>>('table');
  private readonly headBox = viewChild<ElementRef<HTMLElement>>('head');
  private readonly headSentinel = viewChild<ElementRef<HTMLElement>>('headSentinel');
  private readonly pinnedTable = viewChild<ElementRef<HTMLTableElement>>('pinnedTable');

  readonly showSkeleton = computed(() => this.loading() && this.data().length === 0 && !(this.groupable() && this.groups().length > 0));

  readonly skeletonIndexes = computed(() => Array.from({ length: Math.max(0, this.skeletonRows()) }, (_, i) => i));

  @ContentChildren(TemplateForDirective) templateDirectives!: QueryList<TemplateForDirective>;

  private templatesMap = new Map<string, TemplateRef<unknown>>();

  // Inline editing state
  editingCell: { rowId: string | number; field: string } | null = null;
  editingValue: string = '';
  originalValue: string = '';

  onSort = output<ISortOutput>();
  onInlineEdit = output<IInlineEditOutput>();
  onGroupToggle = output<{ groupId: string; expanded: boolean }>();
  rowDragStart = output<{ item: Row; event: DragEvent }>();
  rowDragEnd = output<void>();
  rowReorder = output<IRowReorderOutput>();

  /**
   * Open groups, owned by the page (e.g. "Expand all"). Left null, the table
   * keeps its own: a group header click opens or closes it.
   */
  expandedGroupIds = input<readonly string[] | null>(null);

  // Track expanded groups (used when expandedGroupIds is not bound)
  expandedGroups: Set<string> = new Set();

  ngAfterViewInit(): void {
    this.templateDirectives.forEach((dir) => this.templatesMap.set(dir.appTemplateFor(), dir.templateRef));
    this.cdr.detectChanges();
    if (typeof IntersectionObserver !== 'undefined' && typeof ResizeObserver !== 'undefined') {
      this.zone.runOutsideAngular(() => this.watchPinnedHeader());
    }
    this.zone.runOutsideAngular(() => this.watchReorderDrag());
  }

  /**
   * Keeps the header row in view while the table scrolls past the top of its
   * scroll area. Plain CSS sticky does the pinning; this only notices when it
   * is stuck (an IntersectionObserver on a sentinel above the table, no scroll
   * events), so the stuck look and the scroll padding that keeps a focused row
   * clear of the header apply only then. A table wider than its box scrolls
   * sideways, which stops its header sticking to the page, so a copy of the
   * header row is pinned above it, kept to the real columns' widths and the
   * sideways offset.
   */
  private watchPinnedHeader(): void {
    const host = this.elementRef.nativeElement;
    const box = this.scrollBox()?.nativeElement;
    const table = this.tableBox()?.nativeElement;
    const sentinel = this.headSentinel()?.nativeElement;
    if (!box || !table || !sentinel) return;

    const scroller = this.stickyHeader() ? box : findScrollContainer(host);
    const paddingTarget = scroller ?? host.ownerDocument.documentElement;
    host.style.setProperty('--_stick-top', `${this.stickyHeader() ? 0 : stickyInset(scroller, 'top')}px`);
    let passedTop = false;
    let tableInView = false;
    let stuck = false;

    const apply = (): void => {
      const next = passedTop && tableInView && (this.pinHeader() || this.stickyHeader());
      if (next === stuck) return;
      stuck = next;
      host.classList.toggle('is-head-stuck', stuck);
      const head = this.headBox()?.nativeElement;
      requestScrollPadding(paddingTarget, 'top', this, stuck && head ? head.getBoundingClientRect().height : null);
    };

    // The sentinel's root reaches far below the visible area, so "below" and
    // "in view" read alike and a jump straight past the top (a scrollTop
    // change, Home/End) still crosses a threshold.
    const sentinelIo = new IntersectionObserver(
      ([entry]) => {
        if (entry) passedTop = sentinelPassedTop(entry);
        apply();
      },
      { root: scroller, rootMargin: '0px 0px 100000px 0px', threshold: 0 },
    );
    const tableIo = new IntersectionObserver(
      ([entry]) => {
        if (entry) tableInView = entry.isIntersecting;
        apply();
      },
      { root: scroller, threshold: 0 },
    );
    sentinelIo.observe(sentinel);
    tableIo.observe(table);

    // Sideways scrolling: on when the table is wider than its box.
    const syncCopy = (): void => {
      const copy = this.pinnedTable()?.nativeElement;
      if (!copy) return;
      const real = this.headBox()?.nativeElement.querySelectorAll<HTMLElement>('th') ?? [];
      const cells = copy.querySelectorAll<HTMLElement>('th');
      copy.style.width = `${table.getBoundingClientRect().width}px`;
      real.forEach((th, i) => {
        if (cells[i]) cells[i].style.width = `${th.getBoundingClientRect().width}px`;
      });
      copy.style.marginLeft = `${-box.scrollLeft}px`;
    };
    const measure = (): void => {
      this.fitColumns(box, table);
      const wider = table.getBoundingClientRect().width > box.clientWidth + 1;
      if (wider !== this.scrollsX()) {
        this.zone.run(() => {
          this.scrollsX.set(wider);
          this.cdr.detectChanges();
        });
      }
      syncCopy();
    };
    const ro = new ResizeObserver(() => measure());
    ro.observe(box);
    ro.observe(table);
    const onSideways = (): void => {
      const copy = this.pinnedTable()?.nativeElement;
      if (copy) copy.style.marginLeft = `${-box.scrollLeft}px`;
    };
    box.addEventListener('scroll', onSideways, { passive: true });

    this.destroyRef.onDestroy(() => {
      sentinelIo.disconnect();
      tableIo.disconnect();
      ro.disconnect();
      box.removeEventListener('scroll', onSideways);
      requestScrollPadding(paddingTarget, 'top', this, null);
    });
  }

  isDropped(col: IColumnDef): boolean {
    return this.droppedSet().has(col);
  }

  /**
   * Hides optional columns, lowest `dropOrder` first, until the table fits its
   * box. A new box width or new rows start again with every column shown, so
   * columns come back when there is room. Runs inside the resize observer, so
   * the in-between layouts are never painted.
   */
  private fitColumns(box: HTMLElement, table: HTMLElement): void {
    const optional = this.optionalColumns();
    if (optional.length === 0 && this.droppedColumns().length === 0) return;
    const data = this.data();
    const fresh = box.clientWidth !== this.fitBoxWidth || data !== this.fitData;
    this.fitBoxWidth = box.clientWidth;
    this.fitData = data;

    let dropped = fresh ? [] : this.droppedColumns().filter((col) => optional.includes(col));
    const apply = (): void => {
      this.zone.run(() => {
        this.droppedColumns.set(dropped);
        this.cdr.detectChanges();
      });
    };
    if (dropped.length !== this.droppedColumns().length) apply();
    while (table.getBoundingClientRect().width > box.clientWidth + 1 && dropped.length < optional.length) {
      dropped = [...dropped, optional[dropped.length]];
      apply();
    }
  }

  // Group expansion methods
  toggleGroup(groupId: string): void {
    const isExpanded = this.isGroupExpanded(groupId);
    if (this.expandedGroupIds() === null) {
      if (isExpanded) {
        this.expandedGroups.delete(groupId);
      } else {
        this.expandedGroups.add(groupId);
      }
    }
    this.onGroupToggle.emit({ groupId, expanded: !isExpanded });
  }

  isGroupExpanded(groupId: string): boolean {
    const controlled = this.expandedGroupIds();
    return controlled ? controlled.includes(groupId) : this.expandedGroups.has(groupId);
  }

  isSortable(column: IColumnDef): boolean {
    return !!column.sortable && !!column.field;
  }

  /** The column's current sort, from `sortState` when given, else from the column itself. */
  sortDirectionOf(column: IColumnDef): 'asc' | 'desc' | undefined {
    const state = this.sortState();
    if (state) return state.field === column.field ? state.direction : undefined;
    return column.sortDirection;
  }

  ariaSort(column: IColumnDef): 'ascending' | 'descending' | 'none' | null {
    if (!this.isSortable(column)) return null;
    const direction = this.sortDirectionOf(column);
    return direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none';
  }

  sort(column: IColumnDef): void {
    if (!this.isSortable(column) || !column.field) return;

    const current = this.sortDirectionOf(column);
    if (column.fixedSortDirection && current === column.fixedSortDirection) return;
    const direction = column.fixedSortDirection ?? (current ? (current === 'asc' ? 'desc' : 'asc') : (column.firstSortDirection ?? 'asc'));
    this.columns().forEach((col) => {
      if (col !== column && col.sortDirection !== undefined) col.sortDirection = undefined;
    });
    column.sortDirection = direction;
    this.sortMessage.set(`Sorted by ${column.headerText}, ${direction === 'asc' ? 'ascending' : 'descending'}`);
    this.onSort.emit({ field: column.field, direction });
  }

  isEnd(column: IColumnDef): boolean {
    if (column.align) return column.align === 'end';
    return column.columnType === 'number' || column.columnType === 'actions';
  }

  cellKind(column: IColumnDef): CellKind {
    const type = column.columnType;
    if (type === 'template' || (type === 'actions' && column.field && this.templatesMap.has(column.field))) {
      return column.field ? 'template' : 'empty';
    }
    if (!column.field) return 'empty';
    if (type === 'datetime') return 'datetime';
    if (type === 'date') return 'date';
    if (type === 'number') return 'number';
    return 'text';
  }

  isChecked(item: Row): boolean {
    return this.checkedKeys().has(item[this.rowKey()]);
  }

  checkLabel(item: Row): string {
    const name = item[this.rowLabelKey()];
    return name ? `Select ${String(name)}` : 'Select row';
  }

  /**
   * A row's checkbox was clicked; the box has already flipped. With Shift held,
   * every row between the last clicked one and this one takes the new state.
   */
  onRowCheck(event: MouseEvent, item: Row): void {
    const checked = (event.target as HTMLInputElement).checked;
    const key = item[this.rowKey()];
    const keys = this.data().map((row) => row[this.rowKey()]);
    let affected: unknown[] = [key];
    if (event.shiftKey && this.rangeAnchor !== null) {
      const from = keys.indexOf(this.rangeAnchor);
      const to = keys.indexOf(key);
      if (from !== -1 && to !== -1) affected = keys.slice(Math.min(from, to), Math.max(from, to) + 1);
    }
    this.rangeAnchor = key;
    const next = new Set<unknown>(this.selectedKeys());
    for (const k of affected) {
      if (checked) next.add(k);
      else next.delete(k);
    }
    this.selectedKeys.set([...next] as (string | number)[]);
  }

  /** Checks every row shown, or unchecks them all when they already are. */
  toggleAllShown(): void {
    const keys = this.data().map((row) => row[this.rowKey()]);
    const next = new Set<unknown>(this.selectedKeys());
    const clear = this.pageSelection() === 'all';
    for (const k of keys) {
      if (clear) next.delete(k);
      else next.add(k);
    }
    this.rangeAnchor = null;
    this.selectedKeys.set([...next] as (string | number)[]);
  }

  /** Moves focus to the select-all box, e.g. after a selection toolbar closes. */
  focusSelectAll(): void {
    this.elementRef.nativeElement.shadowRoot?.querySelector<HTMLInputElement>('.select-all')?.focus();
  }

  onRowDragStart(event: DragEvent, item: Row): void {
    if (!this.rowDraggable() || this.editingCell) {
      event.preventDefault();
      return;
    }
    this.rowDragStart.emit({ item, event });
    if (this.rowReorderable()) {
      this.reorderItem = item;
      this.reorderRow = event.currentTarget as HTMLElement;
      this.reorderRow.classList.add('row--dragging');
    }
  }

  onRowDragEnd(): void {
    this.endReorderDrag();
    this.rowDragEnd.emit();
  }

  onRowDrop(event: DragEvent, target: Row): void {
    const item = this.reorderItem;
    const drop = item ? this.dropPlace(event, event.currentTarget as HTMLElement, target) : null;
    this.endReorderDrag();
    if (!item || !drop) return;
    event.preventDefault();
    if (drop.place) this.rowReorder.emit({ item, target, place: drop.place });
  }

  /** Rings the row with this key for a moment, to show where a moved row went. */
  showMoved(key: unknown): void {
    if (this.movedTimer) clearTimeout(this.movedTimer);
    this.movedKey.set(key);
    this.movedTimer = setTimeout(() => {
      this.movedTimer = null;
      this.movedKey.set(null);
    }, 1600);
  }

  isMoved(item: Row): boolean {
    const key = this.movedKey();
    return key !== null && item[this.rowKey()] === key;
  }

  /**
   * Dragover and dragleave fire many times a second during a drag, so they are
   * handled outside Angular and the drop line is set on the row element
   * directly; nothing else on the page is checked while the pointer moves.
   */
  private watchReorderDrag(): void {
    const table = this.tableBox()?.nativeElement;
    if (!table) return;
    const rowAt = (event: Event): { row: HTMLElement; item: Row } | null => {
      const row = (event.target as Element | null)?.closest?.('tr[data-row]') as HTMLElement | null;
      if (!row || row.parentElement?.parentElement !== table) return null;
      const item = this.data()[Number(row.dataset['row'])];
      return item ? { row, item } : null;
    };
    const onDragOver = (event: DragEvent): void => {
      if (!this.reorderItem) return;
      const at = rowAt(event);
      const drop = at ? this.dropPlace(event, at.row, at.item) : null;
      if (!at || !drop) {
        this.setDropMark(null);
        return;
      }
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      this.setDropMark(drop.place ? { row: at.row, place: drop.place } : null);
    };
    // Drag events carry no relatedTarget in Chromium, and a dragleave follows
    // every crossing between cells; clear only once the pointer is off the rows.
    const onDragLeave = (event: DragEvent): void => {
      const body = table.tBodies[0]?.getBoundingClientRect();
      const { clientX: x, clientY: y } = event;
      if (!body || x < body.left || x >= body.right || y < body.top || y >= body.bottom) this.setDropMark(null);
    };
    table.addEventListener('dragover', onDragOver);
    table.addEventListener('dragleave', onDragLeave);
    this.destroyRef.onDestroy(() => {
      table.removeEventListener('dragover', onDragOver);
      table.removeEventListener('dragleave', onDragLeave);
      if (this.movedTimer) clearTimeout(this.movedTimer);
    });
  }

  /**
   * Whether the dragged row may be dropped on `target`, and where: before or
   * after it, by the nearer edge. `place` is null when the drop is allowed but
   * changes nothing (its own row, or the edge next to its own place), so the
   * pointer doesn't show "no drop" right where the drag started.
   */
  private dropPlace(event: DragEvent, row: HTMLElement, target: Row): { place: 'before' | 'after' | null } | null {
    const item = this.reorderItem;
    if (!item || !this.rowReorderable()) return null;
    if (item === target) return { place: null };
    const canReorder = this.canReorder();
    if (canReorder && !canReorder(item, target)) return null;
    const box = row.getBoundingClientRect();
    const place = event.clientY < box.top + box.height / 2 ? 'before' : 'after';
    const rows = this.data();
    const from = rows.indexOf(item);
    const to = rows.indexOf(target);
    if (from !== -1 && to !== -1 && to === (place === 'before' ? from + 1 : from - 1)) return { place: null };
    return { place };
  }

  private setDropMark(mark: { row: HTMLElement; place: 'before' | 'after' } | null): void {
    const current = this.dropMark;
    if (current?.row === mark?.row && current?.place === mark?.place) return;
    current?.row.classList.remove(`row--drop-${current.place}`);
    mark?.row.classList.add(`row--drop-${mark.place}`);
    this.dropMark = mark;
  }

  private endReorderDrag(): void {
    this.setDropMark(null);
    this.reorderRow?.classList.remove('row--dragging');
    this.reorderRow = null;
    this.reorderItem = null;
  }

  isSelected(item: Row): boolean {
    const key = this.selectedKey();
    return key !== null && item[this.rowKey()] === key;
  }

  selectionAria(item: Row): 'true' | 'false' | null {
    if (this.selectedKey() === null) return null;
    return this.isSelected(item) ? 'true' : 'false';
  }

  rowId(item: Row): unknown {
    return item[this.rowKey()];
  }

  isExpanded(item: Row): boolean {
    const key = this.expandedKey();
    return key !== null && item[this.rowKey()] === key;
  }

  /** Element id of a row's detail row, for `aria-controls` on the control that opens it. */
  detailId(key: unknown): string {
    return `row-detail-${String(key)}`;
  }

  /** The template for the no-rows row: the `stateTemplate` input, else an `appTemplateFor="state"` template. */
  get activeStateTemplate(): TemplateRef<unknown> | null {
    return this.stateTemplate() ?? this.getTemplate('state');
  }

  get showStateRow(): boolean {
    if (this.data().length > 0 || this.showSkeleton()) return false;
    if (this.groupable() && this.groups().length > 0) return false;
    return this.activeStateTemplate !== null;
  }

  formatDate(value: string): string {
    if (!value) return '';
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt.toRelative() || '' : '';
  }

  getTemplate(key: string): TemplateRef<unknown> | null {
    return this.templatesMap.get(key) || null;
  }

  /** Scrolls a focused control fully into the table's scroll area, ring included. */
  revealFocused(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    target?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }

  onCellDblClick(column: IColumnDef, item: Row): void {
    if (column.columnType === 'template' && column.editable && column.field) {
      this.startEditing(item, column.field);
    }
  }

  // Inline editing methods
  startEditing(item: Row, field: string): void {
    if (this.editingCell) {
      this.cancelEditing();
    }

    this.editingCell = { rowId: item['id'] as string | number, field };

    // Template columns can map to a differently named property (name -> title).
    this.originalValue = this.getFieldValue(item, field);
    this.editingValue = this.originalValue;

    // Focus the input after the view updates
    setTimeout(() => {
      const parent = this.elementRef.nativeElement.shadowRoot?.querySelector('.inline-edit-input');
      const input = parent?.shadowRoot?.querySelector<HTMLInputElement>('.if-input');
      input?.focus();
    });
  }

  private getFieldValue(item: Row, field: string): string {
    if (item[field] !== undefined) {
      return String(item[field] || '');
    }

    switch (field) {
      case 'name':
        return String(item['name'] || item['title'] || item['fullname'] || '');
      case 'user':
        return String(item['email'] || item['fullname'] || item['username'] || '');
      case 'description':
        return String(item['description'] || item['desc'] || '');
      default:
        return '';
    }
  }

  saveEditing(item: Row): void {
    if (!this.editingCell || this.editingValue === this.originalValue) {
      this.cancelEditing();
      return;
    }

    // Update the item with the new value for immediate UI feedback
    this.updateItemField(item, this.editingCell.field, this.editingValue);

    this.onInlineEdit.emit({
      item,
      field: this.editingCell.field,
      newValue: this.editingValue,
      originalValue: this.originalValue,
    });

    this.cancelEditing();
  }

  private updateItemField(item: Row, field: string, value: string): void {
    const has = (key: string): boolean => Object.prototype.hasOwnProperty.call(item, key);
    if (has(field)) {
      item[field] = value;
      return;
    }

    switch (field) {
      case 'name':
        if (has('name')) item['name'] = value;
        else if (has('title')) item['title'] = value;
        else if (has('fullname')) item['fullname'] = value;
        break;
      case 'user':
        if (has('email')) item['email'] = value;
        else if (has('fullname')) item['fullname'] = value;
        else if (has('username')) item['username'] = value;
        break;
      case 'description':
        if (has('description')) item['description'] = value;
        else if (has('desc')) item['desc'] = value;
        break;
      default:
        item[field] = value;
    }
  }

  cancelEditing(): void {
    this.editingCell = null;
    this.editingValue = '';
    this.originalValue = '';
  }

  isEditing(item: Row, field: string): boolean {
    return this.editingCell?.rowId === item['id'] && this.editingCell?.field === field;
  }

  onKeyDown(event: KeyboardEvent, item: Row): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveEditing(item);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditing();
    }
  }
}
